import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import unitConversionService, { UnitConversion } from '../../services/unitConversionService';
import '../Products/ProductCategoryDetailPage.css';
import { ArrowLeft, PenLine, Trash2 } from 'lucide-react';

const UnitConversionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [conversion, setConversion] = useState<UnitConversion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConversion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadConversion = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await unitConversionService.getUnitConversionById(parseInt(id || '0', 10));
      if (data) {
        setConversion(data);
      }
      setLoading(false);
    } catch (err) {
      setError(t('unitConversion.failedToLoadConversion'));
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!conversion) return;
    navigate(`/products/unit-conversions/${conversion.unitConversionId}/delete`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!conversion) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{error || t('unitConversion.conversionNotFound')}</div>
        </div>
      </Layout>
    );
  }

  const langCode = i18n.language === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="detail-container">
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/products/unit-conversions')}
            title={t('unitConversion.title')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('unitConversion.title')}
          </button>
        </nav>

        <div className="detail-header">
          <h1 className="detail-title">{conversion.fromUnitName} → {conversion.toUnitName}</h1>
          <p className="detail-subtitle">{t('common.view')}</p>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('unitConversion.title')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('unitConversion.fromUnit')}</label>
                    <p className="detail-value"><strong>{conversion.fromUnitName}</strong></p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('unitConversion.toUnit')}</label>
                    <p className="detail-value"><strong>{conversion.toUnitName}</strong></p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('unitConversion.conversionFactor')}</label>
                    <p className="detail-value detail-code">{conversion.conversionFactor}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.status')}</label>
                    <div className="detail-value">
                      <span className="info-badge" style={{ backgroundColor: conversion.isActive ? '#28a745' : '#6c757d' }}>
                        {conversion.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                  </div>

                  <div className="detail-field detail-field-full">
                    <label className="detail-label">{t('unitConversion.remarks')}</label>
                    <p className="detail-value detail-description">
                      {conversion.remarks || <em className="detail-empty">{t('common.noDescription')}</em>}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('common.information')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdDate')}</label>
                    <p className="detail-value">
                      {conversion.createdDate ? new Date(conversion.createdDate).toLocaleDateString(langCode) : '-'}
                    </p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdTime')}</label>
                    <p className="detail-value">
                      {conversion.createdDate ? new Date(conversion.createdDate).toLocaleTimeString(langCode) : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.actions')}</h3>
              </div>
              <div className="detail-card-body detail-actions">
                <button
                  className="btn-save"
                  onClick={() => navigate(`/products/unit-conversions/${conversion.unitConversionId}/edit`)}
                  title={t('common.edit')}
                >
                  <PenLine size={16} strokeWidth={2} />
                  {t('common.edit')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  title={t('common.delete')}
                >
                  <Trash2 size={16} strokeWidth={2} />
                  {t('common.delete')}
                </button>

                <div className="detail-divider"></div>

                <button
                  className="detail-btn detail-btn-secondary"
                  onClick={() => navigate('/products/unit-conversions')}
                  title={t('unitConversion.title')}
                >
                  <ArrowLeft size={16} strokeWidth={2} />
                  {t('unitConversion.title')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnitConversionDetailPage;
